import { useCallback } from 'react';
import type { ReactNode } from 'react';
import toast from 'react-hot-toast';
import { Dialog } from 'voyager';
import type { DialogTone } from 'voyager';

import { useTranslations } from '@lang';

import { DialogSlide } from './Notifications.styles';

export type ShowDialogOptions = {
  tone: DialogTone;
  id: string;
  subtitle?: ReactNode;
  cta?: ReactNode;
  duration?: number;
  dismissLabel?: string;
  onDismiss?: () => void;
};

const DIALOG_POSITION = 'top-right' as const;

export const useDialog = () => {
  const { t } = useTranslations();

  const show = useCallback(
    (title: ReactNode, options: ShowDialogOptions) => {
      const { tone, id, subtitle, cta, duration = Infinity, onDismiss } = options;
      const dismissLabel = options.dismissLabel ?? t('bookingRequestModal.dismissAlert');

      return toast.custom(
        (active) => (
          <DialogSlide $isVisible={active.visible} $isMeasured={Boolean(active.height)}>
            <Dialog
              tone={tone}
              title={title}
              subtitle={subtitle}
              cta={cta}
              dismissLabel={dismissLabel}
              onDismiss={onDismiss ? () => onDismiss() : undefined}
            />
          </DialogSlide>
        ),
        { id, duration, position: DIALOG_POSITION }
      );
    },
    [t]
  );

  const dismiss = useCallback((id: string) => toast.dismiss(id), []);

  return { show, dismiss };
};
